import { Component, ElementRef, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { PlatformCatalogService } from 'src/app/services/platform-catalog.service';

@Component({
  selector: 'app-platform-tab-list',
  templateUrl: './platform-tab-list.component.html',
  styleUrls: ['./platform-tab-list.component.scss'],
})
export class PlatformTabListComponent implements OnInit, OnDestroy {
  constructor(
    private api: PlatformCatalogService,
    private route: ActivatedRoute,
    private el: ElementRef
  ) {}

  private locateSubscription: Subscription | undefined;
  private highlightTimer: any = null;
  /** 当前滚动动画的取消句柄（新定位 / 用户主动操作 / 组件销毁时中断） */
  private cancelScroll: (() => void) | null = null;
  private locateGen = 0;
  private destroyed = false;

  ngOnInit(): void {
    this.findPlatformCategories();
    // 搜索定位请求：自动切换分类 → 平滑滚动 → 高亮卡片
    this.locateSubscription = this.api.locateRequested$.subscribe(
      (platformId) => this.locatePlatform(platformId)
    );
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.locateSubscription?.unsubscribe();
    this.stopScrollAnimation();
    if (this.highlightTimer) {
      clearTimeout(this.highlightTimer);
      this.highlightTimer = null;
    }
  }

  categories: any[] = [];
  loading = false;
  activeCategory: any = null;

  async findPlatformCategories() {
    try {
      this.loading = true;
      // 与搜索共用同一份数据源（请求去重 + 缓存），保证单一数据源
      const categories = await this.api.load();
      this.categories = categories;
      // 默认选中第一个分类，或根据 URL 参数 i 定位
      const i = parseInt(this.route.snapshot.queryParams['i']);
      if (i >= 0 && i < this.categories.length) {
        this.activeCategory = this.categories[i];
      } else {
        this.activeCategory = this.categories[0];
      }
    } catch (error) {
    } finally {
      this.loading = false;
    }
  }

  setActiveCategory(category: any) {
    this.activeCategory = category;
  }

  get activePlatforms(): any[] {
    return this.activeCategory?.platforms || [];
  }

  /** 搜索定位：切换分类 → 缓动滚动 → 快到达时点亮卡片 */
  private locatePlatform(platformId: number) {
    if (!this.categories.length) {
      return;
    }
    // 当前分类里已有该商品就留在原地（不打断用户正在浏览的分类）；
    // 否则跳到商品的首要分类（自动跳过「全部」聚合分类）
    const inCurrent = (this.activeCategory?.platforms || []).some(
      (p: any) => p.id === platformId
    );
    const category = inCurrent
      ? this.activeCategory
      : this.api.categoryOf(platformId);
    if (!category) {
      return;
    }
    this.setActiveCategory(category);

    // 中断上一次定位的滚动，避免两个动画互相争抢
    this.stopScrollAnimation();
    const gen = ++this.locateGen;

    // 等待 *ngFor 重新渲染后再定位
    setTimeout(() => {
      if (this.destroyed || gen !== this.locateGen) {
        return;
      }
      const card = document.getElementById('platform-card-' + platformId);
      if (!card) {
        return;
      }
      const headerEl = document.querySelector('app-page-header');
      const panelEl = this.el.nativeElement.querySelector('.category-panel');
      const headerHeight = headerEl
        ? headerEl.getBoundingClientRect().height
        : 0;
      const panelHeight = panelEl ? panelEl.getBoundingClientRect().height : 0;
      const targetY = Math.max(
        card.getBoundingClientRect().top +
          window.scrollY -
          headerHeight -
          panelHeight -
          12,
        0
      );

      // 系统开启「减少动态效果」时直接跳转，不做动画
      const reduceMotion =
        typeof window.matchMedia === 'function' &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reduceMotion) {
        window.scrollTo(0, targetY);
        this.highlightCard(card);
        return;
      }

      this.animateScrollTo(targetY, card, gen, () => this.highlightCard(card));
    }, 80);
  }

  /**
   * 自定义缓动滚动（替代浏览器原生 smooth）：
   * - easeInOutCubic 缓入缓出，时长随距离伸缩（380~1100ms），节奏一致
   * - 距目标不足 160px 时提前点亮卡片，光晕在滚动收尾时自然浮现，
   *   避免「停住 → 顿一下 → 再亮」的割裂感
   * - 用户滚轮 / 触摸 / 点击 / 键盘翻页时立即让位，不与用户抢滚动
   */
  private animateScrollTo(
    targetY: number,
    card: HTMLElement,
    gen: number,
    onDone: () => void
  ) {
    this.stopScrollAnimation();

    const startY = window.scrollY;
    const dist = targetY - startY;
    if (Math.abs(dist) < 2) {
      // 目标本就在视口内：直接点亮
      onDone();
      return;
    }

    const duration = Math.min(
      1100,
      Math.max(380, Math.round(Math.abs(dist) * 0.5))
    );
    const startTime = performance.now();
    let rafId = 0;
    let finished = false;
    let lit = false;

    const inView = () => {
      const r = card.getBoundingClientRect();
      return r.bottom > 0 && r.top < window.innerHeight;
    };
    /** 只点亮一次；用户中途接管时仅当卡片可见才点亮（避免光晕打在屏外） */
    const lightUp = () => {
      if (lit || gen !== this.locateGen || !inView()) {
        return;
      }
      lit = true;
      onDone();
    };

    // 用户主动操作 → 立即把滚动控制权还给用户
    const wheel = () => this.cancelScroll?.();
    const touch = () => this.cancelScroll?.();
    const mouse = () => this.cancelScroll?.();
    const key = (e: Event) => {
      const k = (e as KeyboardEvent).key;
      if (
        ['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' '].includes(
          k
        )
      ) {
        this.cancelScroll?.();
      }
    };
    window.addEventListener('wheel', wheel, { passive: true });
    window.addEventListener('touchstart', touch, { passive: true });
    window.addEventListener('mousedown', mouse);
    window.addEventListener('keydown', key);

    const cancel = () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('wheel', wheel);
      window.removeEventListener('touchstart', touch);
      window.removeEventListener('mousedown', mouse);
      window.removeEventListener('keydown', key);
      if (this.cancelScroll === cancel) {
        this.cancelScroll = null;
      }
      if (!finished) {
        finished = true;
        lightUp();
      }
    };
    this.cancelScroll = cancel;

    const step = (now: number) => {
      if (finished) {
        return;
      }
      const t = Math.min(1, (now - startTime) / duration);
      const eased = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
      const y = startY + dist * eased;
      window.scrollTo(0, y);

      // 收尾阶段：光晕先浮现，滚动继续走完最后一段
      if (Math.abs(targetY - window.scrollY) < 160) {
        lightUp();
      }
      if (t >= 1) {
        cancel();
        return;
      }
      rafId = requestAnimationFrame(step);
    };
    rafId = requestAnimationFrame(step);
  }

  private stopScrollAnimation() {
    if (this.cancelScroll) {
      this.cancelScroll();
      this.cancelScroll = null;
    }
  }

  /** 高亮卡片：先移除旧 class 以重启动画，动画自然结束后恢复原状 */
  private highlightCard(card: HTMLElement) {
    if (this.highlightTimer) {
      clearTimeout(this.highlightTimer);
      this.highlightTimer = null;
    }

    let fallbackTimer: any = null;
    const cleanup = (e?: AnimationEvent) => {
      // 只响应高亮动画本身（animationend 会冒泡，避免被卡片内其它动画干扰）
      if (e && e.animationName !== 'buy-search-highlight') {
        return;
      }
      card.classList.remove('search-highlight');
      card.removeEventListener('animationend', cleanup);
      if (fallbackTimer) {
        clearTimeout(fallbackTimer);
        fallbackTimer = null;
      }
    };

    card.addEventListener('animationend', cleanup);
    card.classList.remove('search-highlight');
    void card.offsetWidth; // 强制重排，确保动画可以重新播放
    card.classList.add('search-highlight');
    // 兜底：即使 animationend 未触发（如标签页切换），也保证恢复原状
    fallbackTimer = setTimeout(cleanup, 2500);
    this.highlightTimer = fallbackTimer;
  }
}
